import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AuctionCard, cardState, useCountdown } from "@/components/AuctionCard";
import { useAuth } from "@/lib/auth";
import { Label, CardPhoto, money } from "@/components/auction/AuctionUI";
import { Search, Zap, Plus } from "lucide-react";
import { TermsLink } from "@/components/auction/AuctionTerms";
import { HitSectionLoading, useLoadingLevel } from "@/components/loading";

const GAME_TABS = [
  { key: "pokemon", label: "Pokémon" },
  { key: "yugioh", label: "Yu-Gi-Oh!" },
  { key: "riftbound", label: "Riftbound" },
  { key: "other", label: "Otros" },
];

function TurboBanner() {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[#34D399]/40 p-5 lg:p-6" data-testid="turbo-banner"
         style={{ background: "linear-gradient(160deg, rgba(52,211,153,.10), #0c0f0d 55%)" }}>
      <span className="sx-h absolute -top-3 right-2 text-[110px] text-transparent select-none pointer-events-none" style={{ WebkitTextStroke: "1px rgba(52,211,153,.14)" }}>24H</span>
      <div className="relative">
        <div className="sx-mono text-[10px] tracking-[.14em] text-[#34D399] flex items-center gap-1.5"><Zap size={12} /> NUEVO</div>
        <h2 className="sx-h text-[28px] lg:text-[34px] mt-2">Subasta Turbo <span className="text-[#34D399]">gratis</span></h2>
        <p className="text-sm text-white/80 mt-2">1 día de subasta que <b className="text-white">no te cuesta NADA</b>. Solo se cobran los días extra.</p>
        <p className="text-xs text-white/55 mt-1.5">SIN comisiones para HitTCG en cualquier subasta: lo que pujan es tuyo.</p>
        <Link to="/subastas/crear" data-testid="turbo-banner-cta"
              className="mt-5 h-[52px] rounded-full bg-[#34D399] hover:bg-[#4ee0aa] text-[#080808] font-extrabold flex items-center justify-center gap-2 transition-colors">
          <Zap size={15} fill="currentColor" /> Crear Subasta Turbo
        </Link>
      </div>
    </div>
  );
}

function ResultRow({ a }) {
  const st = cardState(a);
  const c = useCountdown(st.clockAt);
  return (
    <Link to={`/subastas/${a.id}`} data-testid={`auction-result-row-${a.id}`} style={{ opacity: a.is_ended ? 0.6 : 1 }}
          className="sx-card flex items-center gap-3.5 p-3 hover:border-white/25 transition-colors">
      <CardPhoto path={a.photos?.[0]} alt={a.product_name} glow={st.tone} w={52} h={72} size={200} rotate={0} />
      <div className="flex-1 min-w-0">
        <div className="font-extrabold truncate">{a.product_name}</div>
        <div className="text-xs text-white/50">{st.priceLabel}</div>
        <div className="sx-price text-lg mt-0.5" style={{ color: st.priceColor }}>{st.priceText || money(st.price)}</div>
      </div>
      <span className="sx-mono text-[11px] flex-none" style={{ color: a.is_ended ? "rgba(255,255,255,.45)" : "#FF4438" }}>
        {a.is_ended ? st.badge[1].replace("✓ ", "").toUpperCase() : (c || "").replace(/:\d\d$/, "")}
      </span>
    </Link>
  );
}

function EmptyGame({ label, onBack }) {
  return (
    <div className="relative text-center py-10" data-testid="auctions-empty">
      <div className="absolute left-1/2 top-0 -translate-x-1/2 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,68,56,.16), transparent 66%)" }} />
      <div className="relative mx-auto w-24 h-32 rounded-[10px] border border-dashed border-white/25 rotate-[-8deg] flex items-center justify-center text-white/40 text-3xl">+</div>
      <div className="relative sx-mono text-[10px] tracking-[.14em] text-[#FF4438] mt-6">{label.toUpperCase()} · 0 SUBASTAS</div>
      <h3 className="relative sx-h text-[34px] mt-2">Aún nadie <span className="sx-it">subasta aquí</span></h3>
      <p className="relative text-sm text-white/60 mt-3 max-w-xs mx-auto">Sé el primero: tu subasta aparecerá arriba de todo en esta pestaña.</p>
      <Link to="/subastas/crear" data-testid="auctions-empty-cta" className="relative inline-flex mt-6 h-[52px] px-8 rounded-full bg-[#34D399] text-[#080808] font-extrabold items-center gap-2">
        <Zap size={15} fill="currentColor" /> Crear Subasta Turbo
      </Link>
      <button type="button" onClick={onBack} data-testid="auctions-empty-back" className="relative block mx-auto mt-5 text-sm font-bold text-white/70 hover:text-white">Ver subastas de Pokémon →</button>
    </div>
  );
}

function NoResults({ term, onClear }) {
  return (
    <div className="text-center py-8" data-testid="auctions-search-empty">
      <div className="sx-h text-[64px] text-transparent" style={{ WebkitTextStroke: "1.5px rgba(255,255,255,.25)" }}>0</div>
      <h3 className="sx-h text-[26px] mt-1">Sin resultados para <span className="sx-it">“{term}”</span></h3>
      <p className="text-sm text-white/55 mt-3 max-w-xs mx-auto">No encontramos subastas en este juego. Prueba con el nombre de la carta, el set o la rareza.</p>
      <button type="button" onClick={onClear} className="sx-btn-ghost mt-6 h-12 px-7 text-sm" data-testid="auctions-search-reset">Limpiar búsqueda</button>
    </div>
  );
}

export default function Subastas() {
  const { user } = useAuth();
  const [game, setGame] = useState("pokemon");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [myCount, setMyCount] = useState({ drafts: 0, auctions: 0 });
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [reload, setReload] = useState(0);
  const level = useLoadingLevel(loading);
  const busy = loading || level !== "none";

  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    api.get("/auctions", { params: { game, limit: 100, q: term || undefined } })
      .then((r) => setItems(r.data.results || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [game, term, reload]);

  useEffect(() => {
    api.get("/auctions", { params: { limit: 100 } }).then((r) => {
      const c = {};
      (r.data.results || []).forEach((x) => { c[x.game_slug] = (c[x.game_slug] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/auctions/drafts/mine").catch(() => ({ data: { total: 0 } })),
      api.get("/auctions/mine").catch(() => ({ data: { total: 0 } })),
    ]).then(([d, a]) => setMyCount({ drafts: d.data.total || 0, auctions: a.data.total || 0 }));
  }, [user]);

  const active = items.filter((x) => !x.is_ended).length;
  const tabLabel = GAME_TABS.find((g) => g.key === game)?.label || "";
  const mine = myCount.drafts + myCount.auctions;

  return (
    <div className="sx relative overflow-hidden" data-testid="auctions-page">
      <div className="sx-halo" />
      <div className="relative max-w-[1280px] mx-auto px-5 lg:px-10 pt-8 lg:pt-12 pb-16">
        <div className="grid lg:grid-cols-[1fr_440px] gap-7 lg:gap-12 items-center pb-7 lg:pb-9 lg:border-b border-white/10">
          <div>
            <div className="sx-mono text-[10px] lg:text-[11px] tracking-[.16em] text-[#ff8a80] flex items-center gap-3"><span className="w-7 lg:w-16 h-px bg-[#FF4438]" /> 0% COMISIÓN · TODAS LAS SUBASTAS</div>
            <h1 className="sx-h text-[46px] lg:text-[84px] mt-3">Subastas <span className="sx-it">en vivo</span></h1>
            <p className="text-[15px] lg:text-[17px] text-white/70 mt-3 max-w-lg">Puja por cartas y productos. Subasta Turbo gratis y 0% comisión en todas las subastas.</p>
            <div className="flex gap-2.5 mt-6">
              <Link to="/subastas/crear" data-testid="link-crear-subasta" className="sx-btn flex-1 lg:flex-none h-[52px] px-7 flex items-center justify-center gap-1.5 text-[15px]"><Plus size={16} /> Crear subasta</Link>
              {user && (
                <Link to="/subastas/mis-subastas" data-testid="link-mis-subastas" className="sx-btn-ghost h-[52px] px-6 flex items-center justify-center text-[15px]">Mis subastas{mine > 0 ? ` (${mine})` : ""}</Link>
              )}
            </div>
            <TermsLink testid="subastas-terms-link" className="mt-4" />
          </div>
          <TurboBanner />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-2 lg:mt-7">
          <div className="flex gap-2 overflow-x-auto -mx-5 px-5 lg:mx-0 lg:px-0 pb-1">
            {GAME_TABS.map((g) => (
              <button key={g.key} type="button" onClick={() => setGame(g.key)} data-testid={`tab-game-${g.key}`}
                      className={`h-10 px-4 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${game === g.key ? "bg-[#FF4438] text-[#080808]" : "border border-white/15 text-white/80 hover:border-white/35"}`}>
                {g.label} <span className={game === g.key ? "opacity-70" : "text-white/40"}>{counts[g.key] || 0}</span>
              </button>
            ))}
          </div>
          <div className="relative lg:w-[460px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="auctions-search-input"
                   placeholder="Buscar por nombre, set, rareza, juego o ciudad…"
                   className={`sx-input w-full h-[50px] pl-11 pr-24 rounded-full text-[15px] ${search ? "border-[#FF4438]/55 bg-[#FF4438]/[.05]" : ""}`} />
            {search && (
              <button type="button" onClick={() => setSearch("")} data-testid="auctions-search-clear" aria-label="Limpiar búsqueda"
                      className="sx-mono absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] tracking-[.1em] border border-white/20 rounded-full px-3 py-1.5 text-white/60 hover:text-white">BORRAR</button>
            )}
          </div>
        </div>

        {busy && <div className="mt-6 min-h-[60vh]"><HitSectionLoading level={level} loading={loading} section={term ? "search" : "auctions"} grid onRetry={() => setReload((k) => k + 1)} /></div>}
        {!busy && term && (
          items.length === 0 ? <NoResults term={term} onClear={() => setSearch("")} /> : (
            <div className="mt-5">
              <Label className="mb-3">{items.length} resultado{items.length === 1 ? "" : "s"} para “{term}”</Label>
              <div className="grid lg:grid-cols-2 gap-3">{items.map((a) => <ResultRow key={a.id} a={a} />)}</div>
            </div>
          )
        )}
        {!busy && !term && items.length === 0 && <EmptyGame label={tabLabel} onBack={() => setGame("pokemon")} />}
        {!busy && !term && items.length > 0 && (
          <>
            <div className="flex items-center justify-between mt-6 mb-4">
              <Label data-testid="auctions-stats">{active} activa{active === 1 ? "" : "s"} · {items.length - active} finalizada{items.length - active === 1 ? "" : "s"}</Label>
              <span className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438]">CIERRAN ANTES ↓</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {items.map((a, i) => <div key={a.id} className="sx-rise" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}><AuctionCard a={a} /></div>)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
